import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import ReactSlider from 'react-slider';
import { useConfig } from 'shared/config/ConfigContext';
import { LinkButtonTextField } from '@/components/LinkButtonTextField';
import type { ApiQueryParams, NumberRange } from '@/interfaces';

export interface RangeSliderStateProps {
    id: string;
    label: string;
    min: number;
    max: number;
    stepSize: number;
    initialValues?: NumberRange;
    resetOnDefault?: boolean;
    metric?: string;
    displayLabel?: boolean;
}

export interface RangeSliderDispatchProps {
    onChange: (values?: NumberRange) => void;
    triggerFetch?: (apiUrl: string, queryParam?: ApiQueryParams) => void;
}

type SliderProps = RangeSliderStateProps & RangeSliderDispatchProps;

export const RangeSlider = ({
    id,
    label,
    min,
    max,
    stepSize,
    initialValues,
    resetOnDefault = false,
    metric = '',
    onChange,
    triggerFetch,
    displayLabel = true,
}: SliderProps) => {
    const { apiUrl } = useConfig();
    const [showInput, setShowInput] = useState(false);
    const [values, setValues] = useState<NumberRange>(initialValues || [min, max]);

    const valueChangeTimeout = useRef<NodeJS.Timeout | null>(null);

    const blurGroupTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect((): void => {
        setValues(initialValues || [min, max]);
    }, [initialValues, min, max]);

    const updateState = (price: NumberRange): void => {
        if (!resetOnDefault || price[0] !== min || price[1] !== max) {
            onChange(price);
        } else {
            onChange(undefined);
        }
        triggerFetch?.(apiUrl);
    };

    const onSliderChange = (steps: NumberRange): void => {
        if (valueChangeTimeout.current) {
            clearTimeout(valueChangeTimeout.current);
        }
        valueChangeTimeout.current = setTimeout((): void => {
            updateState(steps);
        }, 400);

        setValues(steps);
    };

    const onBlur = (): void => {
        blurGroupTimeout.current = setTimeout((): void => {
            setShowInput(false);
            updateState(values);
        }, 10);
    };

    const onFocus = (): void => {
        if (blurGroupTimeout.current) {
            clearTimeout(blurGroupTimeout.current);
        }
    };

    const onClick = (): void => setShowInput(true);

    const onTextChange = ({ low = values[0], high = values[1] }: { low?: number; high?: number }): void => {
        setValues([low, high]);
    };

    const restrictKeys = (event: KeyboardEvent): void => {
        const regex = /[0-9]+/g;
        if (!regex.test(event.key)) {
            event.preventDefault();
        }
    };

    return (
        <div className="u-mv16">
            {displayLabel && <h3 className="u-t5">{label}</h3>}
            <div className="grid grid--cols1to2">
                <LinkButtonTextField
                    id={`${id}-Min`}
                    label={`Minimum ${label}`}
                    type="number"
                    value={values[0].toString()}
                    metric={metric}
                    showInput={showInput}
                    onClick={onClick}
                    onChange={(event): void => onTextChange({ low: ~~event.target.value })}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyPress={restrictKeys}
                    data-testid={`RangeSliderText-Min-${id}`}
                />
                <LinkButtonTextField
                    id={`${id}-Max`}
                    label={`Maks ${label}`}
                    type="number"
                    prefix={values[0] === min && values[1] === max ? 'over' : undefined}
                    value={values[1].toString()}
                    metric={metric}
                    showInput={showInput}
                    onClick={onClick}
                    onChange={(event): void => onTextChange({ high: ~~event.target.value })}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyPress={restrictKeys}
                    floatRight
                    data-testid={`RangeSliderText-Max-${id}`}
                />
            </div>
            <div data-testid={`RangeSliderWrapper-${id}`}>
                <ReactSlider
                    ariaLabel={[`Minimum ${label}`, `Maks ${label}`]}
                    // TODO should there be aria at all here as the values are shown above
                    // ariaValuetext={(state) => (renderValue ? renderValue(state.valueNow) : undefined)}
                    min={min}
                    max={max}
                    value={values}
                    step={stepSize}
                    onChange={setValues}
                    onAfterChange={onSliderChange}
                    className="slider"
                    thumbClassName="slider__thumb"
                    trackClassName="slider__track"
                />
            </div>
        </div>
    );
};
